import { useParams, Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { InternalLinks } from "@/components/InternalLinks";
import { findLicense, licenses } from "@/data/licenses";
import { NotFoundPage } from "./NotFoundPage";

export function LicensePage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { t, lang, prefix } = useI18n();
  const isSv = lang === "sv";
  const aliasMap: Record<string, string> = {
    "swedish-license": "svensk-licens",
    estonia: "estland",
  };
  const lookupSlug = aliasMap[slug] ?? slug;
  const lic = findLicense(lookupSlug);
  if (!lic) return <NotFoundPage />;
  const title = isSv ? lic.name_sv : lic.name_en;
  const desc = isSv ? lic.description_sv : lic.description_en;
  const otherLicenses = licenses.filter((l) => l.id !== lic.id);

  return (
    <div className="container-page py-8">
      <SEO title={title} description={desc} />
      <Breadcrumbs
        items={[
          { label: t("nav.home"), href: prefix },
          {
            label: isSv ? "Licenser" : "Licenses",
            href: `${prefix}/${isSv ? "licenser" : "licenses"}`,
          },
          { label: title },
        ]}
      />
      <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">{title}</h1>
      <p className="mt-2 text-sm font-semibold uppercase tracking-wider text-brand-700">
        {isSv ? "Jurisdiktion: " : "Jurisdiction: "}
        {lic.jurisdiction}
      </p>
      <p className="mt-3 max-w-3xl text-lg text-ink-700">{desc}</p>

      <article className="prose prose-ink mt-8 max-w-3xl [&>h2]:mt-8 [&>h2]:mb-3 [&>h2]:text-2xl [&>h2]:font-bold [&>p]:my-3 [&>ul]:my-3 [&>ul]:list-disc [&>ul]:pl-6">
        <h2>{isSv ? "Vad licensen omfattar" : "What the license covers"}</h2>
        {licenseLong(lic.slug, isSv)}
        <h2>{isSv ? "Hur jämför sig licensen?" : "How does it compare?"}</h2>
        <p>
          {isSv
            ? "Svensk licens har bland de strängaste konsumentskydden i Europa eftersom den kräver Spelpaus, BankID och en bonus per spelare. MGA är näst strängast och en del av EU. Curaçao har historiskt sett mildare krav. Estland ligger någonstans mittemellan. UKGC är mycket strikt men gäller enbart Storbritannien."
            : "The Swedish license has some of the strictest consumer protection in Europe because it mandates Spelpaus, BankID and one bonus per player. MGA is the second strictest and is inside the EU. Curaçao has historically had lighter requirements. Estonia sits somewhere between. UKGC is very strict but only applies to the United Kingdom."}
        </p>
      </article>

      <h2 className="mt-12 text-2xl font-bold">{isSv ? "Andra licenser" : "Other licenses"}</h2>
      <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        {otherLicenses.map((o) => (
          <Link
            key={o.id}
            to={`${prefix}/${isSv ? "licenser" : "licenses"}/${o.slug}`}
            className="card-interactive p-4 no-underline"
          >
            <h3 className="text-sm font-semibold">{isSv ? o.name_sv : o.name_en}</h3>
            <p className="mt-1 text-xs text-ink-500">{o.jurisdiction}</p>
          </Link>
        ))}
      </div>

      <InternalLinks context="license" />
    </div>
  );
}

function licenseLong(slug: string, isSv: boolean) {
  switch (slug) {
    case "svensk-licens":
      return isSv ? (
        <>
          <p>
            Svensk spellicens utfärdas av Spelinspektionen. Det finns flera typer:
            kommersiellt onlinespel, vadhållning, spel för allmännyttiga ändamål
            och landbaserade kasinon. Den vanligaste licensen för online är
            kommersiellt onlinespel.
          </p>
          <ul>
            <li>Anslutning till spelpaus.se — obligatorisk.</li>
            <li>BankID-identifiering vid registrering.</li>
            <li>Insättningsgräns, förlustgräns och tidsgräns.</li>
            <li>En bonus per spelare.</li>
            <li>Skattefria vinster för dig som privatperson.</li>
            <li>Tvister hanteras enligt svensk konsumentlag (ARN).</li>
          </ul>
        </>
      ) : (
        <>
          <p>
            The Swedish gambling license is issued by Spelinspektionen. Several
            types exist: commercial online gambling, betting, gambling for the
            public benefit, and land-based casinos. The most common online
            license is commercial online gambling.
          </p>
          <ul>
            <li>Mandatory connection to spelpaus.se.</li>
            <li>BankID identification at registration.</li>
            <li>Deposit, loss and time limits.</li>
            <li>One bonus per player.</li>
            <li>Tax-free winnings for you as a private individual.</li>
            <li>Disputes handled under Swedish consumer law (ARN).</li>
          </ul>
        </>
      );
    case "mga":
      return isSv ? (
        <p>
          Malta Gaming Authority (MGA) är en av EU:s större spellicenser. Den ger
          ett rimligt konsumentskydd och innebär att operatören följer EU:s
          regler kring penningtvätt, spelaridentifiering och uttagshantering.
          MGA är inte ansluten till svenska Spelpaus.
        </p>
      ) : (
        <p>
          The Malta Gaming Authority (MGA) is one of the EU's larger gambling
          licenses. It provides reasonable consumer protection and means the
          operator follows EU rules on money laundering, player identification
          and withdrawals. MGA is not connected to Sweden's Spelpaus.
        </p>
      );
    case "curacao":
      return isSv ? (
        <p>
          Curaçao-licens har historiskt sett ett betydligt lägre krav än både
          svensk licens och MGA. Det är en vanlig licens för internationella
          casinon med stora bonusar och kryptobetalningar. Konsumentskyddet är
          lägre och tvister kan vara svårare att driva.
        </p>
      ) : (
        <p>
          Curaçao licensing has historically had significantly lower
          requirements than both the Swedish license and MGA. It is a common
          choice for international casinos with big bonuses and crypto payments.
          Consumer protection is lower and disputes can be harder to pursue.
        </p>
      );
    case "estland":
      return isSv ? (
        <p>
          Estnisk licens utfärdas av Estonian Tax and Customs Board och är
          vanligt på pay-and-play-sajter utanför svensk reglering. Estland är
          medlem i EU så vissa av MGA:s standarder gäller, men Spelpaus är inte
          en del av estnisk licens.
        </p>
      ) : (
        <p>
          The Estonian license is issued by the Estonian Tax and Customs Board
          and common at pay-and-play sites outside Swedish regulation. Estonia
          is part of the EU so some MGA standards apply, but Spelpaus is not
          part of the Estonian license.
        </p>
      );
    case "ukgc":
      return isSv ? (
        <p>
          UK Gambling Commission har bland de strängaste kraven i världen — men
          den gäller endast brittiska spelare. En UKGC-licens säger inget om
          svensk konsumenträtt eller Spelpaus.
        </p>
      ) : (
        <p>
          The UK Gambling Commission has some of the strictest requirements
          worldwide — but it only applies to UK players. A UKGC license says
          nothing about Swedish consumer rights or Spelpaus.
        </p>
      );
    default:
      return null;
  }
}
